<template>
  <div class="flex flex-col min-h-screen bg-slate-50">
    <Header />
    <FlashMessage v-if="messages.text !== ''" :message="messages" />

    <div class="flex-1 py-7">
      <div class="mx-auto max-w-[1200px] px-4 sm:px-6">
        <!-- Page Header -->
        <div
          class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6"
        >
          <div>
            <h1 class="text-2xl font-extrabold text-gray-800 tracking-tight">
              Survey Management
              <span class="ml-2 text-sm font-normal text-gray-500">
                ({{ surveycount || 0 }})
              </span>
            </h1>
            <p class="text-sm text-gray-500 mt-0.5">
              Create and manage surveys for students
            </p>
          </div>
          <router-link
            :to="{ name: 'SurveyCreate' }"
            class="inline-flex items-center gap-2 bg-blues hover:bg-hover text-white font-semibold px-5 py-2.5 rounded-xl shadow-sm transition-all text-sm whitespace-nowrap"
          >
            <svg
              class="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2.5"
                d="M12 4v16m8-8H4"
              />
            </svg>
            Create New Survey
          </router-link>
        </div>

        <!-- Main Card -->
        <div
          class="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden p-4"
        >
          <!-- Filters Row -->
          <div class="flex flex-col sm:flex-row gap-3 items-center">
            <!-- Search -->
            <div class="relative flex-1 max-w-sm">
              <span
                class="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none"
              >
                <svg
                  class="w-4 h-4 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </span>
              <input
                v-model="searchQuery"
                type="text"
                @input="fetchSurveyData(1)"
                placeholder="Search surveys..."
                class="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <!-- Status Filter -->
            <select
              v-model="publishedFilter"
              @change="fetchSurveyData(1)"
              class="px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Status</option>
              <option value="true">Published</option>
              <option value="false">Unpublished</option>
            </select>

            <!-- Clear -->
            <button
              @click="clearFilters"
              class="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
              title="Clear Filters"
            >
              <svg
                class="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <!-- Table -->
          <div class="overflow-x-auto mt-4 border rounded-lg">
            <table class="min-w-full divide-y divide-gray-100">
              <thead class="bg-gray-100">
                <tr class="text-left text-xs font-semibold text-textblues">
                  <th class="px-4 py-3 border-b border-r w-10">S.No</th>
                  <th class="px-4 py-3 border-b border-r min-w-[220px]">
                    Survey Title
                  </th>
                  <th class="px-4 py-3 border-b border-r w-28 text-center">
                    Questions
                  </th>

                  <th class="px-4 py-3 border-b border-r w-24 text-center">
                    Responses
                  </th>
                  <th class="px-4 py-3 border-b border-r w-24 text-center">
                    Status
                  </th>
                  <th class="px-4 py-3 border-b border-r w-28">Created At</th>
                  <th class="px-4 py-3 border-b w-28 text-center">Actions</th>
                </tr>
              </thead>

              <!-- Loading Skeleton -->
              <tbody v-if="loading" class="divide-y divide-gray-50">
                <tr v-for="n in 5" :key="n" class="animate-pulse">
                  <td class="px-4 py-3">
                    <div class="h-3 w-6 bg-gray-100 rounded"></div>
                  </td>
                  <td class="px-4 py-3">
                    <div class="h-3 w-52 bg-gray-200 rounded mb-1.5"></div>
                    <div class="h-2.5 w-36 bg-gray-100 rounded"></div>
                  </td>
                  <td class="px-4 py-3">
                    <div class="h-3 w-10 bg-gray-100 rounded"></div>
                  </td>
                  <td class="px-4 py-3">
                    <div class="h-5 w-24 bg-gray-100 rounded-full"></div>
                  </td>
                  <td class="px-4 py-3">
                    <div class="h-3 w-8 bg-gray-100 rounded"></div>
                  </td>
                  <td class="px-4 py-3">
                    <div class="h-5 w-16 bg-gray-100 rounded-full"></div>
                  </td>
                  <td class="px-4 py-3">
                    <div class="h-3 w-20 bg-gray-100 rounded"></div>
                  </td>
                  <td class="px-4 py-3">
                    <div class="h-7 w-24 bg-gray-100 rounded-lg mx-auto"></div>
                  </td>
                </tr>
              </tbody>

              <!-- Data -->
              <tbody
                v-else-if="surveys.length > 0"
                class="divide-y divide-gray-100 text-xs"
              >
                <tr
                  v-for="(survey, index) in surveys"
                  :key="survey.id"
                  class="hover:bg-slate-50 transition-colors"
                >
                  <!-- S.No -->
                  <td
                    class="px-3 py-2 border-r text-center text-xs font-medium text-gray-500"
                  >
                    {{ (currentPage - 1) * pageSize + index + 1 }}
                  </td>

                  <!-- Survey -->
                  <td class="px-3 py-2 border-r w-64">
                    <router-link
                      :to="{ name: 'SurveyResponses', params: { id: survey.id } }"
                      class="text-sm font-semibold text-blue-600 hover:text-blue-800 hover:underline cursor-pointer line-clamp-2"
                    >
                      {{ survey.title }}
                    </router-link>

                    <p class="mt-1 text-[11px] text-slate-400 line-clamp-2">
                      {{ survey.description }}
                    </p>
                  </td>

                  <!-- Questions -->
                  <td
                    class="px-3 py-2 border-r text-center text-xs font-medium"
                  >
                    {{ survey.question_count }}
                  </td>

                  <!-- Responses -->
                  <td
                    class="px-3 py-2 border-r text-center text-xs font-medium text-slate-700"
                  >
                    {{ survey.student_count }}
                  </td>

                  <!-- Status -->
                  <td class="px-3 py-2 border-r text-center">
                    <div class="flex justify-center">
                      <button
                        @click="toggleStatus(survey)"
                        :title="
                          survey.is_published
                            ? 'Unpublish Survey'
                            : 'Publish Survey'
                        "
                        :class="[
                          'w-8 h-4 flex items-center rounded-full p-0.5 transition-colors duration-300 focus:outline-none',
                          survey.is_published
                            ? 'bg-emerald-500'
                            : 'bg-slate-300',
                        ]"
                      >
                        <span
                          :class="[
                            'bg-white w-3 h-3 rounded-full shadow transform transition-transform duration-300',
                            survey.is_published
                              ? 'translate-x-4'
                              : 'translate-x-0',
                          ]"
                        ></span>
                      </button>
                    </div>
                  </td>

                  <!-- Created -->
                  <td
                    class="px-3 py-2 border-r text-[11px] text-gray-500 whitespace-nowrap"
                  >
                    {{ formatDate(survey.created_at) }}
                  </td>

                  <!-- Actions -->
                  <td class="px-3 py-2">
                    <div class="flex items-center justify-center gap-1">
                      <!-- Edit -->
                      <router-link
                        :to="{ name: 'SurveyEdit', params: { id: survey.id } }"
                        class="w-7 h-7 flex items-center justify-center rounded-md text-blue-600 hover:bg-blue-50 transition"
                        title="Edit"
                      >
                        <svg
                          class="w-3.5 h-3.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                          />
                        </svg>
                      </router-link>

                      <!-- Delete -->
                      <button
                        @click="deleteSurvey(survey)"
                        class="w-7 h-7 flex items-center justify-center rounded-md text-red-500 hover:bg-red-50 transition"
                        title="Delete"
                      >
                        <svg
                          class="w-3.5 h-3.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>

              <!-- Empty State -->
              <tbody v-else>
                <tr>
                  <td colspan="8" class="py-16 text-center">
                    <div class="flex flex-col items-center gap-3">
                      <div
                        class="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center"
                      >
                        <svg
                          class="w-8 h-8 text-slate-300"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="1.5"
                            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                          />
                        </svg>
                      </div>
                      <p class="text-slate-500 font-semibold">
                        No surveys found
                      </p>
                      <p class="text-slate-400 text-xs">
                        Try adjusting your filters or create a new survey.
                      </p>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div
            v-if="surveycount >= 11"
            class="flex flex-col sm:flex-row justify-center gap-3 mt-4 relative"
          >
            <div
              class="md:absolute md:left-3 md:top-4 gap-2 text-sm text-gray-600 flex justify-center items-center md:flex-none md:justify-normal"
            >
              <span class="flex md:hidden">Rows per page:</span>
              <select
                v-model="pageSize"
                @change="onPageSizeChange"
                class="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option :value="10">10</option>
                <option :value="20">20</option>
                <option :value="50">50</option>
                <option :value="100">100</option>
              </select>
            </div>
            <div class="flex justify-center">
              <Pagination
                :totalPages="totalPages"
                ref="paginationRef"
                @page-changed="fetchSurveyData"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div
      v-if="showDeleteModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4 transition-opacity"
    >
      <div class="w-full max-w-sm rounded-3xl bg-white p-6 shadow-2xl border border-slate-100 text-center">
        
        <!-- Trash Icon -->
        <div class="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-50 text-red-500 mb-4 relative">
          <svg class="w-8 h-8 relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </div>

        <h3 class="text-[19px] font-extrabold text-slate-800 mb-2">Delete Survey</h3>
        
        <p class="text-sm text-slate-500 mx-auto leading-relaxed px-1">
          Are you sure you want to permanently delete
          <span class="font-bold text-slate-700 px-0.5">"{{ selectedSurveyId?.title }}"</span>? This action cannot be undone.
        </p>

        <div class="mt-7 flex gap-3">
          <button
            @click="showDeleteModal = false"
            class="flex-1 rounded-xl bg-slate-50 border border-slate-200 py-3 text-[13px] font-bold text-slate-600 hover:bg-slate-100 hover:text-slate-700 transition-colors"
          >
            Cancel
          </button>

          <button
            @click="confirmDelete"
            class="flex-1 rounded-xl bg-red-600 py-3 text-[13px] font-bold text-white shadow-lg shadow-red-200 hover:bg-red-700 hover:shadow-red-300 transition-all active:scale-[0.98]"
          >
            Yes, Delete
          </button>
        </div>
      </div>
    </div>
    <div
      v-if="showPopup"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
    >
      <div
        class="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
      >
        <!-- Header -->
        <div class="px-6 pt-6 flex flex-col items-center">
          <div
            :class="[
              'w-14 h-14 rounded-full flex items-center justify-center mb-4',
              selectedSurvey.is_published ? 'bg-red-100' : 'bg-emerald-100',
            ]"
          >
            <svg
              v-if="selectedSurvey.is_published"
              class="w-7 h-7 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636"
              />
            </svg>

            <svg
              v-else
              class="w-7 h-7 text-emerald-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>

          <h2 class="text-xl font-semibold text-slate-800 text-center">
            {{ selectedSurvey.is_published ? "Unpublish " : "Publish " }}
          </h2>

          <p class="mt-2 text-sm text-slate-500 text-center leading-6">
            Are you sure you want to
            <span class="font-semibold text-slate-700">
              {{ selectedSurvey.is_published ? "unpublish" : "publish" }}
            </span>
            the survey
            <br />
            <span class="font-semibold text-blue-600">
              "{{ selectedSurvey.title }}"
            </span>
            ?
          </p>
        </div>

        <!-- Footer -->
        <div class="mt-8 px-6 py-5 bg-slate-50 flex justify-end gap-3">
          <button
            @click="cancelToggle"
            class="px-5 py-2 text-sm font-medium rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-100"
          >
            Cancel
          </button>

          <button
            @click="confirmToggle"
            :class="[
              'px-5 py-2 text-sm font-medium rounded-lg text-white ',
              selectedSurvey.is_published
                ? 'bg-red-600 hover:bg-red-700'
                : 'bg-emerald-600 hover:bg-emerald-700',
            ]"
          >
            {{ selectedSurvey.is_published ? "Unpublish" : "Publish" }}
          </button>
        </div>
      </div>
    </div>
    <div
      v-if="showAlreadyPublishedPopup"
      class="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4 transition-opacity"
    >
      <div class="w-full max-w-sm rounded-3xl bg-white p-6 shadow-2xl border border-slate-100 text-center">
        <!-- Icon Badge -->
        <div class="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-amber-50 text-amber-500 mb-4 relative">
          <svg class="h-8 w-8 relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
             <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
          </svg>
        </div>

        <!-- Title & Subtitle -->
        <h3 class="text-[19px] font-extrabold text-slate-800 mb-2">Replace Active Survey?</h3>
        <p class="text-sm text-slate-500 mx-auto leading-relaxed px-1">
          Only <span class="font-bold text-slate-600">one</span> survey can be published at a time. Replacing will swap their statuses.
        </p>

        <!-- Swap Display Card -->
        <div class="mt-6 rounded-2xl bg-slate-50 border border-slate-100 p-4 w-full shadow-inner">
          <div class="flex items-center text-left gap-3">
            <div class="w-10 h-10 rounded-full bg-white border border-slate-200 shadow-sm flex items-center justify-center shrink-0 text-rose-500">
               <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 14l-7 7m0 0l-7-7m7 7V3" /></svg>
            </div>
            <div class="flex-1 min-w-0">
              <span class="text-[10px] font-extrabold text-rose-500 uppercase tracking-widest">Unpublishing</span>
              <p class="text-sm font-bold text-slate-700 truncate min-w-0 leading-tight mt-0.5" :title="publishedSurvey?.title">{{ publishedSurvey?.title || "Current Survey" }}</p>
            </div>
          </div>
          
          <div class="ml-5 border-l-2 border-dashed border-slate-200 h-4 my-1"></div>

          <div class="flex items-center text-left gap-3">
            <div class="w-10 h-10 rounded-full bg-white border border-slate-200 shadow-sm flex items-center justify-center shrink-0 text-emerald-500">
               <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 10l7-7m0 0l7 7m-7-7v18" /></svg>
            </div>
            <div class="flex-1 min-w-0">
              <span class="text-[10px] font-extrabold text-emerald-500 uppercase tracking-widest">Publishing</span>
              <p class="text-sm font-bold text-slate-800 truncate min-w-0 leading-tight mt-0.5" :title="selectedSurvey?.title">{{ selectedSurvey?.title || "Selected Survey" }}</p>
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="mt-7 flex gap-3">
          <button
            @click="showAlreadyPublishedPopup = false"
            class="flex-1 rounded-xl bg-slate-50 border border-slate-200 py-3 text-[13px] font-bold text-slate-600 hover:bg-slate-100 hover:text-slate-700 transition-colors"
          >
            Cancel
          </button>

          <button
            @click="confirmSwapPublish"
            class="flex-1 rounded-xl bg-blues py-3 text-[13px] font-bold text-white shadow-lg shadow-blue-200 hover:bg-hover hover:shadow-blue-300 transition-all active:scale-[0.98]"
          >
            Yes, Replace
          </button>
        </div>
      </div>
    </div>
    <Footer />
  </div>
</template>

<script>
import Header from "@/components/Admin/Header.vue";
import FlashMessage from "@/components/FlashMessage.vue";
import axios from "axios";
import { mapGetters } from "vuex";
import Footer from "@/components/Footer.vue";
import Pagination from "@/components/Pagination.vue";

export default {
  name: "SurveyList",
  components: {
    Header,
    FlashMessage,
    Footer,
    Pagination,
  },
  data() {
    return {
      loading: false,
      searchQuery: "",
      statusFilter: "",
      publishedFilter: "",
      currentPage: 1,
      totalPages: 1,
      pageSize: 10,
      deleteTargetId: null,
      messages: { text: "", type: "" },
      surveys: [],
      showPopup: false,
      selectedSurvey: null,
      showDeleteModal: false,
      selectedSurveyId: null,
      showAlreadyPublishedPopup: false,
      publishedSurvey: null,
      surveycount: "",
    };
  },
  computed: {
    ...mapGetters(["userInfo"]),
  },
  created() {
    this.fetchSurveyData(1);
  },
  methods: {
    onPageSizeChange() {
      this.currentPage = 1;
      if (this.$refs.paginationRef) {
        this.$refs.paginationRef.currentPage = 1;
      }
      this.fetchSurveyData(1);
    },
    clearFilters() {
      this.searchQuery = "";
      this.statusFilter = "";
      this.publishedFilter = "";
      this.fetchSurveyData(1);
    },

    deleteSurvey(id) {
      this.selectedSurveyId = id;
      this.showDeleteModal = true;
    },

    async confirmDelete() {
      try {
        const response = await axios.delete(
          `${process.env.VUE_APP_API}/survey/${this.selectedSurveyId.id}/?Key=${this.userInfo.token_key}`,
        );

        if (response.data?.status === 200) {
          this.messages = {
            text: "Survey deleted successfully.",
            type: "success",
          };

          this.fetchSurveyData(1);

          this.showDeleteModal = false;
          this.selectedSurveyId = null;
        } else {
          this.messages = {
            text: response.data?.message || "Failed to delete survey.",
            type: "error",
          };
        }
      } catch (error) {
        this.messages = {
          text:
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message ||
            "Failed to delete survey.",
          type: "error",
        };
      } finally {
        this.showDeleteModal = false;
        this.selectedSurveyId = null;

        setTimeout(() => {
          this.messages = {
            text: "",
            type: "",
          };
        }, 2000);
      }
    },
    async toggleStatus(survey) {
      // Unpublish is always allowed
      if (survey.is_published) {
        this.selectedSurvey = survey;
        this.showPopup = true;
        return;
      }

      // Find an already published survey across ALL surveys (not just the
      // current paginated page), since the active one may be on another page
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API}/survey/list/without-pagination/?Key=${this.userInfo.token_key}&is_published=true`,
        );

        if (response.data?.status === 200) {
          const activeSurvey = response.data.data.find(
            (item) => item.id !== survey.id,
          );

          if (activeSurvey) {
            this.publishedSurvey = activeSurvey;
            this.selectedSurvey = survey;
            this.showAlreadyPublishedPopup = true;
            return;
          }
        }
      } catch (error) {
        console.error("Error checking published survey:", error);
      }

      // No published survey found
      this.selectedSurvey = survey;
      this.showPopup = true;
    },

    async confirmSwapPublish() {
      try {
        const payload = {
          title: this.publishedSurvey.title,
          description: this.publishedSurvey.description,
          is_published: false,
          start_date: this.publishedSurvey.start_date || null,
          end_date: this.publishedSurvey.end_date || null,
        };

        const response = await axios.patch(
          `${process.env.VUE_APP_API}/survey/${this.publishedSurvey.id}/?Key=${this.userInfo.token_key}`,
          payload,
        );

        if (response.data.status === 200) {
          this.publishedSurvey.is_published = false;
          this.showAlreadyPublishedPopup = false;
          await this.confirmToggle();
        }
      } catch (error) {
        console.error("Failed to unpublish old survey", error);
        this.messages = {
          text: "Failed to automatically unpublish the old survey.",
          type: "error",
        };
        this.showAlreadyPublishedPopup = false;
      }
    },

    cancelToggle() {
      this.showPopup = false;
      this.selectedSurvey = null;
    },

    async confirmToggle() {
      try {
        const payload = {
          title: this.selectedSurvey.title,
          description: this.selectedSurvey.description,
          is_published: !this.selectedSurvey.is_published,
          start_date: this.selectedSurvey.start_date || null,
          end_date: this.selectedSurvey.end_date || null,
        };

        const response = await axios.patch(
          `${process.env.VUE_APP_API}/survey/${this.selectedSurvey.id}/?Key=${this.userInfo.token_key}`,
          payload,
        );

        if (response.data.status === 200) {
          this.selectedSurvey.is_published = !this.selectedSurvey.is_published;

          this.messages = {
            text:
              response.data.message || "Survey status updated successfully.",
            type: "success",
          };

          await this.fetchSurveyData(this.currentPage);
        }
      } catch (error) {
        console.error(error);

        this.messages = {
          text:
            error.response?.data?.message ||
            error.response?.data?.error ||
            "Failed to update survey status.",
          type: "error",
        };
      } finally {
        this.showPopup = false;
        this.selectedSurvey = null;

        setTimeout(() => {
          this.messages = {
            text: "",
            type: "",
          };
        }, 2000);
      }
    },
    formatDate(date) {
      if (!date) return "";

      const d = new Date(date);

      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");

      let hours = d.getHours();
      const minutes = String(d.getMinutes()).padStart(2, "0");
      const seconds = String(d.getSeconds()).padStart(2, "0");

      const ampm = hours >= 12 ? "pm" : "am";
      hours = hours % 12 || 12;
      hours = String(hours).padStart(2, "0");

      return `${yyyy}-${mm}-${dd}, ${hours}:${minutes}:${seconds} ${ampm}`;
    },
    async fetchSurveyData(page) {
      this.currentPage = page;
      this.isLoading = true;
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API}/survey/list/?Key=${this.userInfo.token_key}&search=${this.searchQuery}&status=${this.statusFilter}&is_published=${this.publishedFilter}&page=${page}&page_size=${this.pageSize}`,
        );
        if (response.data.data.status == 200) {
          this.surveys = response.data.data.data;
          this.totalPages = response.data.total_page;
          this.current_page = response.data.current_page;
          this.surveycount = response.data.total;
        } else {
          this.surveys = [];
        }
      } catch (error) {
        console.error("Error fetching survey data:", error);
      } finally {
        this.isLoading = false;
      }
    },
  },
  watch: {},
};
</script>
